/**
 * Ambient knowledge-graph mesh behind the signed-in app shell — a faint
 * constellation of nodes (hubs = domains, satellites = projects) wired by thin
 * lines, echoing the product concept. Pure SVG, no image files; colours come
 * from the brand tokens (--accent / --teal) so it re-themes automatically, and
 * it's kept low-opacity so content stays legible. Fixed + pointer-events-none.
 * The clusters sit toward the edges/corners, leaving a calm centre for content.
 */

// Coordinates in a 1440x900 space; the SVG is sliced to cover the viewport.
const NODES: readonly { x: number; y: number; r: number }[] = [
  { x: 170, y: 250, r: 3.2 }, // 0  left hub
  { x: 70, y: 180, r: 1.8 }, //  1
  { x: 250, y: 165, r: 1.8 }, // 2
  { x: 110, y: 355, r: 1.8 }, // 3
  { x: 275, y: 345, r: 2 }, //   4
  { x: 1210, y: 250, r: 3.4 }, // 5  right-upper hub
  { x: 1320, y: 185, r: 1.8 }, // 6
  { x: 1110, y: 180, r: 1.8 }, // 7
  { x: 1300, y: 340, r: 2 }, //   8
  { x: 1090, y: 330, r: 1.8 }, // 9
  { x: 1240, y: 410, r: 1.8 }, // 10
  { x: 1180, y: 700, r: 3.2 }, // 11 right-lower hub
  { x: 1300, y: 645, r: 1.8 }, // 12
  { x: 1085, y: 760, r: 1.8 }, // 13
  { x: 1285, y: 775, r: 2 }, //   14
  { x: 1180, y: 585, r: 1.8 }, // 15
  { x: 715, y: 120, r: 1.8 }, //  16
  { x: 640, y: 805, r: 1.8 }, //  17
  { x: 835, y: 815, r: 1.8 }, //  18
];

const EDGES: readonly [number, number][] = [
  [0, 1], [0, 2], [0, 3], [0, 4], [2, 4], [1, 3],
  [5, 6], [5, 7], [5, 8], [5, 9], [5, 10], [6, 8], [7, 9],
  [11, 12], [11, 13], [11, 14], [11, 15], [12, 14],
  [5, 11], [16, 5], [16, 2], [17, 11], [18, 14], [17, 18],
];

// A few nodes rendered in teal for brand variation; the rest use the accent.
const TEAL = new Set([4, 8, 12, 16]);

export function AppBackground(): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="dda-app-surface pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <g stroke="var(--accent)" strokeWidth={1} strokeOpacity={0.16}>
          {EDGES.map(([a, b], i) => {
            const p = NODES[a];
            const q = NODES[b];
            if (!p || !q) return null;
            return <line key={i} x1={p.x} y1={p.y} x2={q.x} y2={q.y} />;
          })}
        </g>
        {NODES.map((n, i) => {
          const teal = TEAL.has(i);
          const hub = n.r >= 3;
          return (
            <circle
              key={i}
              cx={n.x}
              cy={n.y}
              r={n.r}
              fill={teal ? 'var(--teal)' : 'var(--accent)'}
              fillOpacity={hub ? 0.55 : teal ? 0.5 : 0.34}
            />
          );
        })}
      </svg>
    </div>
  );
}
